import type { MasteryState } from "@engine/mastery/masteryState.js";
import type { AttemptRecord, EnginePersistence } from "@engine/persistence/types.js";
import type { PlayerState } from "@engine/session.js";
import type { Question } from "@engine/types.js";

const RETRY_LIMIT = 3;
const RETRY_BACKOFF_MS = 250;
const HYDRATE_ATTEMPTS = 50;

type QueueEntry =
  | { kind: "savePlayer"; state: PlayerState }
  | { kind: "saveMastery"; state: MasteryState }
  | { kind: "appendAttempt"; record: AttemptRecord };

type CoalesceKind = "savePlayer" | "saveMastery";

/**
 * In-memory snapshot of an upstream `EnginePersistence` (typically
 * `SupabasePersistence`). Reads are synchronous and hit the cache.
 * Writes are optimistic: the cache is mutated inline, then the entry is
 * enqueued for an async drain to the upstream.
 *
 * Drain is deferred to a microtask so consecutive synchronous calls can be
 * coalesced. `savePlayer` and `saveMastery` collapse a run of like-kind
 * tail entries into the most recent one. `appendAttempt` never coalesces.
 *
 * Each upstream call retries up to `RETRY_LIMIT` times with linear
 * backoff. On final failure, dispatches `window.dispatchEvent(new
 * CustomEvent("snapshot:writeFailed", { detail: { entry, error } }))`
 * so the PWA can surface a toast.
 *
 * Listeners for `visibilitychange → hidden` and `beforeunload` are wired
 * by `hydrate`. Call `close()` on tear-down to remove them and drain the
 * queue.
 */
export class SnapshotPersistence implements EnginePersistence {
  private player: PlayerState | null;
  private mastery: MasteryState | null;
  private attempts: AttemptRecord[];

  private queue: QueueEntry[] = [];
  private drainPromise: Promise<void> | null = null;
  private drainScheduled = false;
  private closed = false;

  private readonly visibilityHandler = (): void => {
    if (typeof document !== "undefined" && document.visibilityState === "hidden") {
      void this.flush();
    }
  };
  private readonly beforeUnloadHandler = (): void => {
    void this.flush();
  };

  static async hydrate(remote: EnginePersistence): Promise<SnapshotPersistence> {
    const [player, mastery, attempts] = await Promise.all([
      Promise.resolve(remote.loadPlayer()),
      Promise.resolve(remote.loadMastery?.() ?? null),
      Promise.resolve(remote.loadAttempts(HYDRATE_ATTEMPTS)),
    ]);
    return new SnapshotPersistence(remote, player ?? null, mastery ?? null, attempts ?? []);
  }

  private constructor(
    private readonly remote: EnginePersistence,
    player: PlayerState | null,
    mastery: MasteryState | null,
    attempts: AttemptRecord[]
  ) {
    this.player = player;
    this.mastery = mastery;
    this.attempts = [...attempts].sort((a, b) => (a.at < b.at ? 1 : -1));
    if (typeof document !== "undefined" && typeof document.addEventListener === "function") {
      document.addEventListener("visibilitychange", this.visibilityHandler);
    }
    if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
      window.addEventListener("beforeunload", this.beforeUnloadHandler);
    }
  }

  loadPlayer(): PlayerState | null {
    return this.player ? { ...this.player } : null;
  }

  savePlayer(state: PlayerState): void {
    this.player = { ...state };
    this.enqueue({ kind: "savePlayer", state: { ...state } }, "savePlayer");
  }

  loadMastery(): MasteryState | null {
    if (!this.mastery) return null;
    return {
      defaultRating: this.mastery.defaultRating,
      byConcept: { ...this.mastery.byConcept },
    };
  }

  saveMastery(state: MasteryState): void {
    this.mastery = {
      defaultRating: state.defaultRating,
      byConcept: { ...state.byConcept },
    };
    this.enqueue(
      {
        kind: "saveMastery",
        state: {
          defaultRating: state.defaultRating,
          byConcept: { ...state.byConcept },
        },
      },
      "saveMastery"
    );
  }

  appendAttempt(record: AttemptRecord): void {
    this.attempts = [{ ...record }, ...this.attempts];
    this.enqueue({ kind: "appendAttempt", record: { ...record } });
  }

  loadAttempts(limit?: number): AttemptRecord[] {
    const out = this.attempts.map((a) => ({ ...a }));
    return typeof limit === "number" ? out.slice(0, limit) : out;
  }

  loadQuestionBank(): Question[] | null {
    return null;
  }

  saveQuestionBank(_questions: Question[]): void {
    // Intentional no-op — `SupabasePersistence` is also a no-op for the
    // question bank; signed-in players read the same generated bank that
    // the PWA rebuilds on every boot.
  }

  /**
   * Drain everything currently queued, awaiting any in-flight drain first.
   * Safe to call repeatedly. Used by visibility/beforeunload handlers and
   * `close()`.
   */
  async flush(): Promise<void> {
    this.drainScheduled = false;
    if (!this.drainPromise && this.queue.length > 0) {
      this.drainPromise = this.runDrain();
    }
    while (this.drainPromise) {
      const inflight = this.drainPromise;
      await inflight;
      if (this.queue.length > 0 && !this.drainPromise) {
        this.drainPromise = this.runDrain();
      }
    }
  }

  async close(): Promise<void> {
    if (this.closed) return;
    this.closed = true;
    if (typeof document !== "undefined" && typeof document.removeEventListener === "function") {
      document.removeEventListener("visibilitychange", this.visibilityHandler);
    }
    if (typeof window !== "undefined" && typeof window.removeEventListener === "function") {
      window.removeEventListener("beforeunload", this.beforeUnloadHandler);
    }
    await this.flush();
  }

  private enqueue(entry: QueueEntry, coalesce?: CoalesceKind): void {
    if (coalesce) {
      const tail = this.queue[this.queue.length - 1];
      if (tail && tail.kind === coalesce) {
        this.queue[this.queue.length - 1] = entry;
      } else {
        this.queue.push(entry);
      }
    } else {
      this.queue.push(entry);
    }
    this.scheduleDrain();
  }

  private scheduleDrain(): void {
    if (this.drainScheduled || this.drainPromise) return;
    this.drainScheduled = true;
    queueMicrotask(() => {
      this.drainScheduled = false;
      if (this.queue.length === 0 || this.drainPromise) return;
      this.drainPromise = this.runDrain();
    });
  }

  private async runDrain(): Promise<void> {
    try {
      while (this.queue.length > 0) {
        const entry = this.queue.shift()!;
        await this.runWithRetry(entry);
      }
    } finally {
      this.drainPromise = null;
    }
  }

  private async runWithRetry(entry: QueueEntry): Promise<void> {
    let lastError: unknown = null;
    for (let attempt = 0; attempt < RETRY_LIMIT; attempt++) {
      try {
        switch (entry.kind) {
          case "savePlayer":
            await this.remote.savePlayer(entry.state);
            return;
          case "saveMastery":
            if (!this.remote.saveMastery) return;
            await this.remote.saveMastery(entry.state);
            return;
          case "appendAttempt":
            await this.remote.appendAttempt(entry.record);
            return;
        }
      } catch (err) {
        lastError = err;
        if (attempt < RETRY_LIMIT - 1) {
          await new Promise((resolve) => setTimeout(resolve, RETRY_BACKOFF_MS * (attempt + 1)));
        }
      }
    }
    if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
      window.dispatchEvent(
        new CustomEvent("snapshot:writeFailed", { detail: { entry, error: lastError } })
      );
    }
  }
}
